<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/duotone-regular-svg-icons'
import { ref } from 'vue'
import { faCheckCircle, faExclamationTriangle, faInfoCircle, faXmark, faXmarkCircle } from '@fortawesome/duotone-regular-svg-icons'
import Button from '~/components/ui/Button.vue'

export type AuCalloutVariant = 'info' | 'success' | 'warning' | 'error'

const {
  variant,
  title,
  dismissible,
} = defineProps<{
  variant: AuCalloutVariant
  title?: string
  dismissible?: boolean
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const open = ref(true)

const icon = {
  info: faInfoCircle,
  success: faCheckCircle,
  warning: faExclamationTriangle,
  error: faXmarkCircle
} as const satisfies Record<AuCalloutVariant, IconDefinition>

const label = {
  info: 'Melding, info',
  success: 'Melding, suksess',
  warning: 'Melding, advarsel',
  error: 'Melding, feil'
} as const satisfies Record<AuCalloutVariant, string>

const classes = {
  info: 'bg-seagreen-pop text-blue-60 border-blue-20',
  success: 'bg-green-pop text-green-60 border-green-20',
  warning: 'bg-yellow-pop text-black border-yellow-50',
  error: 'bg-coral-20 text-coral-60 border-coral-40',
} as const satisfies Record<AuCalloutVariant, string>

const onClose = () => {
  emit('close')
  open.value = false
}
</script>

<template>
  <aside
    v-if="open"
    class="relative w-full rounded-xl p-4 text-left"
    :class="classes[variant]"
    :aria-label="label[variant]"
  >
    <font-awesome-icon
      :icon="icon[variant]"
      size="xl"
      class="absolute mt-[1.5px]"
    />
    <div class="flex flex-col px-10">
      <h3
        v-if="title"
        class="text-lg font-medium"
        v-text="title"
      />
      <slot />
    </div>
    <Button
      v-if="dismissible"
      shape="square"
      size="sm"
      class="absolute top-4 right-4"
      variant="ghost"
      color="gray"
      aria-label="Lukk melding"
      @click="onClose"
    >
      <font-awesome-icon :icon="faXmark" />
    </Button>
  </aside>
</template>
